import { accessUk } from './uk-access'
import { cashUk } from './uk-cash'
import { faceUk } from './uk-face'
import { goodsUk } from './uk-goods'
import { overviewUk } from './uk-overview'
import { passesUk } from './uk-passes'
import { payrollUk } from './uk-payroll'
import { saleUk } from './uk-sale'
import { servicesUk } from './uk-services'

/// Every word the panel shows, in Ukrainian. One place, one shape: adding
/// Russian later is a second object of the same type (`Strings`) and a switch
/// in `i18n/index.ts`, not a hunt through the screens.
///
/// Sentence case everywhere (the iOS design system's rule). Functions are for
/// strings with numbers or names in them; plurals go through `plural`.

export const plural = (n: number, one: string, few: string, many: string): string => {
  const mod10 = n % 10
  const mod100 = n % 100
  if (mod10 === 1 && mod100 !== 11) return one
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return few
  return many
}

const people = (n: number) => `${n} ${plural(n, 'людина', 'людини', 'людей')}`

export const uk = {
  app: {
    name: 'eatlete',
    tagline: 'Панель клубу',
    loading: 'Завантаження…',
    retry: 'Повторити',
    cancel: 'Скасувати',
    close: 'Закрити',
    save: 'Зберегти',
    saving: 'Зберігаємо…',
    saved: 'Збережено',
    back: 'Назад',
    more: 'Дії',
    picker: {
      chooseDate: 'Оберіть дату',
      clear: 'Очистити',
      today: 'Сьогодні',
      noMatches: 'Нічого не знайдено',
    },
    you: 'Ви',
    unnamed: 'Без імені',
  },

  theme: {
    label: 'Тема',
    light: 'Світла',
    dark: 'Темна',
    system: 'Системна',
  },

  signIn: {
    title: 'Вхід до панелі клубу',
    message: 'Увійдіть акаунтом eatlete, з яким вас додали до клубу.',
    inviteMessage: 'Увійдіть акаунтом із поштою, на яку прийшло запрошення, — і ми одразу його приймемо.',
    backToInvite: 'Немає акаунта? Поверніться до запрошення й створіть його',
    email: 'Пошта',
    emailPlaceholder: 'name@example.com',
    password: 'Пароль',
    passwordPlaceholder: 'Ваш пароль',
    showPassword: 'Показати пароль',
    hidePassword: 'Сховати пароль',
    submit: 'Увійти',
    submitting: 'Входимо…',
    codeTitle: 'Код підтвердження',
    codeMessage: 'Введіть 6 цифр із застосунку-автентифікатора.',
    code: 'Код',
    codeSubmit: 'Підтвердити',
    codeSubmitting: 'Перевіряємо…',
    useAnotherAccount: 'Увійти іншим акаунтом',
    footnote:
      'Немає акаунта? Попросіть власника клубу надіслати вам запрошення — акаунт створите прямо за посиланням.',
    errors: {
      emptyEmail: 'Введіть пошту',
      badEmail: 'Це не схоже на пошту',
      emptyPassword: 'Введіть пароль',
      badCode: 'Код — це 6 цифр',
      invalidCredentials: 'Неправильна пошта або пароль',
      socialAccount:
        'Цей акаунт входить через Google або Apple. Задайте пароль у застосунку eatlete, щоб увійти тут.',
      invalidCode: 'Код не підійшов. Перевірте час на телефоні й спробуйте ще раз',
      expiredChallenge: 'Час на код вийшов. Увійдіть ще раз',
      tooManyAttempts: 'Забагато спроб. Спробуйте за кілька хвилин',
    },
  },

  shell: {
    companies: 'Компанії',
    switchCompany: 'Змінити компанію',
    chooseCompany: 'Оберіть компанію',
    allCompanies: 'Усі компанії',
    searchCompanies: 'Пошук компанії',
    noMatches: 'Нічого не знайдено',
    godsSection: 'Боги',
    godsCompanies: 'Компанії',
    godsList: 'Боги',
    companySection: 'Компанія',
    workSection: 'Клуб',
    clients: 'Клієнти',
    staff: 'Співробітники',
    myProfile: 'Мій профіль',
    roles: 'Ролі',
    branches: 'Клуби',
    settings: 'Налаштування',
    signOut: 'Вийти',
    account: 'Обліковий запис',
    menu: 'Меню',
    openMenu: 'Відкрити меню',
    asEatlete: 'Ви тут як eatlete — клуб бачить ваші зміни від «eatlete».',
    noCompaniesTitle: 'Вас ще не додали до жодного клубу',
    noCompaniesMessage:
      'Коли власник клубу додасть вас за поштою, клуб зʼявиться тут. Переконайтеся, що він знає саме цю адресу.',
    nothingForRoleTitle: 'Для вашої ролі тут поки нічого немає',
    nothingForRoleMessage:
      'Розділи панелі відкриваються за роллю. Якщо вам потрібен доступ, попросіть власника або директора змінити вам роль.',
  },

  companies: {
    title: 'Компанії',
    subtitle: 'Усі клуби й мережі на eatlete. Створювати їх можуть лише боги.',
    create: 'Створити компанію',
    empty: 'Ще немає жодної компанії',
    emptyMessage: 'Створіть першу й назвіть її власника — він зможе додати решту команди.',
    status: { active: 'Активна', suspended: 'Призупинена' } as Record<string, string>,
    open: 'Відкрити',
    createdBanner: (name: string) => `Компанію «${name}» створено.`,
    logoFailedTitle: (name: string) => `Компанію «${name}» створено, але логотип не завантажився`,
    logoFailedMessage: (reason: string) =>
      `${reason}. Компанія вже є — додайте логотип ще раз у її налаштуваннях.`,
    openSettings: 'До налаштувань',
    columns: { name: 'Компанія', branches: 'Клуби', created: 'Створено', status: 'Стан' },
    form: {
      title: 'Нова компанія',
      name: 'Назва',
      namePlaceholder: 'Наприклад, «Атлет»',
      owner: 'Власник',
      ownerHint: 'Пошта акаунта eatlete людини, яка керуватиме компанією.',
      ownerFoundFootnote: 'Стане власником. Ви членом компанії не станете.',
      firstBranch: 'Перший клуб',
      firstBranchToggle: 'Додати перший клуб зараз',
      firstBranchFootnote: 'Можна й пізніше. Без клубу компанія ще нічого не продає.',
      branchName: 'Назва клубу',
      branchNamePlaceholder: 'Наприклад, «Оболонь»',
      city: 'Місто',
      cityPlaceholder: 'Київ',
      address: 'Адреса',
      addressPlaceholder: 'вул. Героїв Дніпра, 1',
      logo: 'Логотип',
      logoOptional: 'Необовʼязково',
      submit: 'Створити компанію',
      submitting: 'Створюємо…',
      uploadingLogo: 'Завантажуємо логотип…',
      nameRequired: 'Назвіть компанію',
      ownerRequired: 'Знайдіть власника за поштою',
      branchRequired: 'Назвіть клуб або приберіть перший клуб',
    },
  },

  logo: {
    choose: 'Обрати файл',
    replace: 'Замінити',
    remove: 'Прибрати',
    drop: 'Перетягніть сюди або оберіть файл',
    dropActive: 'Відпустіть, щоб додати',
    hint: 'PNG, JPG або WebP, до 2 МБ. Найкраще — квадратний, від 256 × 256.',
    preview: (name: string) => `Логотип компанії «${name}»`,
    errors: {
      type: 'Підходять лише PNG, JPG або WebP. SVG та інші формати не приймаємо',
      size: 'Файл більший за 2 МБ. Зменште зображення й спробуйте ще раз',
      empty: 'Файл порожній. Оберіть інший',
    },
  },

  settings: {
    title: 'Налаштування',
    stateNow: 'Зараз',
    subtitle: 'Як компанію бачать співробітники й клієнти.',
    company: 'Компанія',
    name: 'Назва',
    nameFootnote: 'Назву бачать співробітники в перемикачі компаній і клієнти в застосунку.',
    nameReadOnly: 'Назву змінює власник або директор.',
    renameTitle: 'Назва компанії',
    nameRequired: 'Вкажіть назву',
    renamed: (name: string) => `Компанію перейменовано на «${name}»`,
    state: 'Стан компанії',
    stateFootnote:
      'Призупинена компанія закрита для всіх співробітників: панель відповідає їм, що доступ призупинено. Дані лишаються. Бачать це лише боги.',
    suspendedBanner: 'Компанію призупинено. Співробітники не можуть увійти, поки ви її не відновите.',
    suspend: 'Призупинити компанію',
    suspendTitle: 'Призупинити компанію?',
    suspendMessage: (name: string) =>
      `Усі співробітники «${name}» втратять доступ до панелі одразу. Дані нікуди не зникнуть — відновити можна будь-коли тут же.`,
    suspendConfirm: 'Призупинити',
    suspended: 'Компанію призупинено',
    restore: 'Відновити компанію',
    restoreTitle: 'Відновити компанію?',
    restoreMessage: 'Співробітники знову зможуть увійти з тими ж ролями й клубами, що були.',
    restoreConfirm: 'Відновити',
    restored: 'Компанію відновлено',
    logo: 'Логотип',
    logoFootnote:
      'Видно в списку компаній, у перемикачі й у бічній панелі. Без логотипа — перші літери назви.',
    upload: 'Завантажити',
    uploading: 'Завантажуємо…',
    uploaded: 'Логотип оновлено',
    removed: 'Логотип прибрано',
    removeTitle: 'Прибрати логотип?',
    removeMessage: 'Замість нього всюди будуть перші літери назви. Новий можна завантажити будь-коли.',
    removeConfirm: 'Прибрати',
    noLogo: 'Логотипа ще немає',
  },

  branches: {
    title: 'Клуби',
    subtitle: 'Зали компанії. Від клубу залежить, де продають і в якому часовому поясі рахують день.',
    add: 'Додати клуб',
    active: 'Відкриті',
    archive: 'Архів',
    archiveFootnote:
      'Архівних клубів немає у виборі, але їхні заняття, продажі й історія лишаються. Повернути можна будь-коли.',
    empty: 'Ще немає жодного клубу',
    emptyMessage:
      'Клуб — це зал: тут продають абонементи, ведуть розклад і пускають на вході. Без клубу компанія ще нічого не продає.',
    emptyNotYours: 'Додати клуб може власник або директор.',
    allArchived: 'Усі клуби в архіві',
    allArchivedMessage: 'Поверніть клуб з архіву або додайте новий — без відкритого клубу компанія нічого не продає.',
    readOnly: 'Відкривати, змінювати й архівувати клуби можуть власник і директор.',
    columns: { name: 'Назва', place: 'Де', zone: 'Часовий пояс', people: 'Люди', status: 'Стан' },
    open: 'Відкритий',
    edit: 'Змінити',
    archiveAction: 'В архів',
    restoreAction: 'Повернути',
    archived: 'В архіві',
    created: (name: string) => `Клуб «${name}» додано.`,
    archivedBanner: (name: string) => `Клуб «${name}» в архіві.`,
    restoredBanner: (name: string) => `Клуб «${name}» знову відкритий.`,
    archiveTitle: (name: string) => `Перенести «${name}» в архів?`,
    archiveMessage:
      'Клуб зникне з вибору для співробітників і продажів. Його заняття, продажі й історія лишаються, а повернути клуб можна будь-коли.',
    archiveOnly: (names: string) =>
      `Лише в цьому клубі працюють: ${names}. Після архіву в них не лишиться жодного відкритого клубу — змініть їм клуби в «Співробітниках».`,
    archiveAlso: (names: string) => `Ще привʼязані до нього серед інших клубів: ${names}.`,
    archiveScoped: (n: number) =>
      `До клубу привʼязано ${people(n)}. Їхні налаштування не зміняться, але цей клуб зникне з їхнього вибору — перегляньте їхні клуби в «Співробітниках».`,
    archiveConfirm: 'В архів',
    restoreTitle: (name: string) => `Повернути «${name}»?`,
    restoreMessage: 'Клуб знову зʼявиться у виборі для співробітників і продажів.',
    restoreConfirm: 'Повернути',
    form: {
      addTitle: 'Новий клуб',
      name: 'Назва',
      namePlaceholder: 'Наприклад, «Оболонь»',
      nameRequired: 'Назвіть клуб',
      city: 'Місто',
      cityPlaceholder: 'Київ',
      address: 'Адреса',
      addressPlaceholder: 'вул. Героїв Дніпра, 1',
      place: 'Де',
      submitAdd: 'Додати клуб',
      submitAdding: 'Додаємо…',
    },
    /// «Години роботи» (specs/partner-clubs.md §1a): the app shows «відкрито
    /// до 22:00» on the club's card from these.
    hours: {
      section: 'Години роботи',
      hint: 'Застосунок покаже клієнтам, чи клуб відкритий зараз і до котрої. Час — за часовим поясом клубу.',
      notSaid: 'Години ще не вказано — у застосунку їх не видно.',
      say: 'Вказати години',
      clear: 'Не вказувати',
      copyMonday: 'Скопіювати пн на будні',
      days: ['Нд', 'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб'] as readonly string[],
      dayNames: ['Неділя', 'Понеділок', 'Вівторок', 'Середа', 'Четвер', 'Пʼятниця', 'Субота'] as readonly string[],
      open: (day: string) => `${day}: відкрито`,
      closed: 'Зачинено',
      allDay: 'Цілодобово',
      from: (day: string) => `${day}: з`,
      to: (day: string) => `${day}: до`,
      addSpan: (day: string) => `${day}: додати проміжок`,
      removeSpan: (day: string, range: string) => `${day}: прибрати ${range}`,
      footnote: 'До 3 проміжків на день — наприклад, з перервою. «до 00:00» — до півночі; кінець раніше за початок — робота після півночі.',
      errors: {
        time: 'Вкажіть час',
        empty: 'Початок і кінець однакові',
        overlap: 'Проміжки перетинаються',
      },
    },
    /// «Обкладинка»: the photo on top of the club's card in the app.
    cover: {
      section: 'Обкладинка',
      hint: 'PNG, JPG або WebP, до 5 МБ. Найкраще — горизонтальне фото залу, від 1200 × 675.',
      footnote: 'Фото над карткою клубу в застосунку. Зберігається одразу.',
      choose: 'Обрати фото',
      replace: 'Замінити',
      remove: 'Прибрати',
      dropActive: 'Відпустіть, щоб додати',
      preview: (name: string) => `Обкладинка клубу «${name}»`,
      empty: 'Обкладинки ще немає',
      uploaded: 'Обкладинку оновлено.',
      removed: 'Обкладинку прибрано.',
      errors: {
        type: 'Підходять лише PNG, JPG або WebP. SVG та інші формати не приймаємо',
        size: 'Файл більший за 5 МБ. Зменште фото й спробуйте ще раз',
        empty: 'Файл порожній. Оберіть інший',
      },
    },
  },

  clients: {
    title: 'Клієнти',
    count: (n: number) => `${n} ${plural(n, 'клієнт', 'клієнти', 'клієнтів')}`,
    subtitleOwn: 'Ті, хто записаний на ваші заняття.',
    subtitleBranches: 'Клієнти ваших клубів і ті, що спільні для всієї мережі.',
    add: 'Новий клієнт',
    search: 'Пошук клієнтів',
    searchPlaceholder: 'Імʼя, телефон, пошта або номер картки',
    searchHint: 'Натисніть /, щоб шукати',
    clearSearch: 'Очистити пошук',
    status: 'Стан',
    statusActive: 'Активні',
    statusArchived: 'Архів',
    branchFilter: 'Клуб',
    allBranches: 'Усі клуби',
    noBranch: 'Уся мережа',
    loadMore: (left: number) => `Показати ще ${left}`,
    loadMoreUnknown: 'Показати ще',
    loadingMore: 'Завантажуємо…',
    shown: (shown: number, total: number) => `Показано ${shown} з ${total}`,
    empty: 'Ще немає жодного клієнта',
    emptyMessage: 'Додайте першого — досить імені й телефону. Решту можна дописати пізніше.',
    emptyNotYours: 'Реєструвати клієнтів може адміністратор рецепції або керуючий.',
    emptyOwn: 'Поки ніхто не записаний на ваші заняття',
    emptyOwnMessage: 'Щойно хтось запишеться на ваше заняття, він зʼявиться тут.',
    emptyArchive: 'В архіві нікого немає',
    emptyArchiveMessage: 'Сюди потрапляють клієнти, яких перенесли в архів. Їхня історія лишається.',
    noMatches: (q: string) => `Нікого не знайдено за «${q}»`,
    noMatchesMessage: 'Перевірте написання або шукайте за телефоном — він найнадійніший.',
    addFromSearch: (q: string) => `Додати «${q}»`,
    archivedBadge: 'архів',
    mergedBadge: 'обʼєднано',
    noContacts: 'Без контактів',
    card: (number: string) => `картка ${number}`,
    created: (name: string) => `Клієнта «${name}» додано.`,
    open: 'Відкрити',
    backToList: 'Клієнти',
    unnamed: 'Без імені',
    erasedName: 'Видалений клієнт',
    columns: {
      client: 'Клієнт',
      phone: 'Телефон',
      email: 'Пошта',
      branch: 'Клуб',
      certificate: 'Довідка',
      added: 'Додано',
    },
    certificateUntil: (date: string) => `до ${date}`,
    certificateExpired: 'прострочена',

    detail: {
      face: {
        title: 'Вхід за обличчям',
        enrolled: 'Обличчя зареєстровано',
        notEnrolled: 'Обличчя не зареєстровано',
        since: (date: string) => `З ${date} — вхід за обличчям працює`,
        hint: 'Камера біля дверей пускатиме без картки й телефона.',
        take: 'Зробити фото',
        retake: 'Перезняти',
        forget: 'Видалити фото',
        forgetTitle: 'Видалити фото обличчя?',
        forgetMessage: 'Вхід за обличчям одразу перестане працювати, а термінали забудуть клієнта після наступної синхронізації.',
        footnote:
          'Зберігаємо фото й шаблон обличчя — 128 чисел, за якими камера біля дверей впізнає клієнта. Фото потрібне лише терміналам входу й для оновлення моделі.',
        needsConsent: 'Спершу клієнт має дати згоду «Вхід за обличчям» — вона вище, у «Згодах».',
        cameraTitle: 'Фото для входу',
        cameraHint: 'Обличчя прямо в камеру, без окулярів від сонця й капюшона, рівне світло.',
        shoot: 'Зняти',
        again: 'Ще раз',
        save: 'Зберегти',
        noCamera: 'Камера недоступна — дозвольте доступ у браузері або підключіть камеру.',
        preview: 'Знімок обличчя',
        live: 'Камера',
      },
      edit: 'Змінити',
      contacts: 'Про клієнта',
      chipCertificateValid: (date: string) => `Довідка до ${date}`,
      chipCertificateExpired: (date: string) => `Довідка прострочена ${date}`,
      chipCertificateMissing: 'Без довідки',
      mergeWith: 'Обʼєднати з дублікатом',
      phone: 'Телефон',
      email: 'Пошта',
      birthDate: 'Дата народження',
      age: (n: number) => `${n} ${plural(n, 'рік', 'роки', 'років')}`,
      cardNumber: 'Номер картки',
      branch: 'Клуб',
      wholeNetwork: 'Уся мережа',
      source: 'Звідки дізнався',
      tags: 'Мітки',
      note: 'Примітка',
      certificate: 'Медична довідка',
      certificateValid: (date: string) => `дійсна до ${date}`,
      certificateExpired: (date: string) => `прострочена ${date}`,
      certificateMissing: 'не надана',
      certificateFootnote: 'Без дійсної довідки клієнта не пустять на вході, але продати абонемент можна.',
      since: (date: string) => `У клубі з ${date}`,
      linkedApp: 'Має застосунок eatlete',
      notSet: '—',
      archivedTitle: 'Клієнт в архіві',
      archivedMessage: 'Його немає в списку активних, але історія та нотатки лишаються.',
      restore: 'Повернути з архіву',
      archive: 'В архів',
      archived: 'Клієнта перенесено в архів.',
      restored: 'Клієнта повернуто з архіву.',
      mergedTitle: 'Цей запис обʼєднано з іншим',
      mergedMessage: 'Тут лише стара копія. Усі зміни робіть у записі, що лишився.',
      openSurvivor: 'Відкрити запис',
      erasedTitle: 'Дані клієнта стерто',
      erasedMessage: 'Імʼя, контакти й нотатки видалено. Відвідування й оплати лишилися в історії клубу без особи.',
      notFound: 'Такого клієнта немає',
      notFoundMessage: 'Можливо, його видалили або він в іншому клубі, до якого у вас немає доступу.',
    },

    passes: {
      title: 'Абонементи',
      placeholder: 'Абонементи клієнта зʼявляться тут',
      placeholderMessage: 'Продаж, заморозка й історія відвідувань — у наступному оновленні панелі.',
    },

    consents: {
      title: 'Згоди',
      personal_data: 'Обробка персональних даних',
      personal_dataHint: 'Без неї клуб не може зберігати картку клієнта.',
      marketing: 'Розсилки',
      marketingHint: 'SMS і листи про акції та новини клубу.',
      biometric: 'Вхід за обличчям',
      biometricHint: 'Фото для термінала біля дверей. Окрема письмова згода, відкликати можна будь-коли.',
      granted: (date: string) => `Надано ${date}`,
      withdrawn: (date: string) => `Відкликано ${date}`,
      never: 'Ще не питали',
      footnote: 'Кожну зміну записуємо з датою — так завжди видно, що клієнт погоджувався й коли.',
      history: 'Історія згод',
      hideHistory: 'Сховати історію',
      event: (purpose: string, granted: boolean) => `${purpose}: ${granted ? 'надано' : 'відкликано'}`,
      source: {
        reception: 'на рецепції',
        app: 'у застосунку',
        import: 'з імпорту',
        phone: 'телефоном',
      } as Record<string, string>,
      toggle: (purpose: string) => `Згода: ${purpose}`,
    },

    notes: {
      title: 'Нотатки',
      placeholder: 'Що варто знати колегам: травми, побажання, домовленості',
      add: 'Додати нотатку',
      submit: 'Додати',
      adding: 'Додаємо…',
      shortcut: (mac: boolean) => (mac ? '⌘ Enter — додати' : 'Ctrl Enter — додати'),
      empty: 'Нотаток ще немає',
      tooLong: 'Не більше 2000 символів',
      by: (name: string) => name,
      byColleague: 'Співробітник',
      byEatlete: 'eatlete',
    },

    timeline: {
      title: 'Історія',
      empty: 'Подій ще немає',
      emptyMessage: 'Тут зʼявляться записи на заняття, скасування й місця з черги.',
      booked: 'Запис на заняття',
      cancelled: 'Скасовано запис',
      cancelledLate: 'Скасовано запис пізно',
      promoted: 'Отримано місце з черги',
      sessionCancelled: (name: string | null, reason: string | null) =>
        `${name ? `Заняття «${name}» скасував клуб` : 'Заняття скасував клуб'}${reason ? ` — ${reason}` : ''}`,
      trainerChanged: (name: string | null, from: string | null, to: string | null) =>
        `Заміна тренера${name ? ` на «${name}»` : ''}${from && to ? `: ${from} → ${to}` : to ? `: тепер ${to}` : ''}`,
      sessionMoved: (name: string | null) => (name ? `Заняття «${name}» перенесли` : 'Заняття перенесли'),
      sessionChanged: (name: string | null) => (name ? `Заняття «${name}» змінилося` : 'Заняття змінилося'),
    },

    duplicates: {
      title: 'Схоже, це та сама людина',
      message: (n: number) =>
        n === 1
          ? 'Є ще один запис, дуже схожий на цей.'
          : `Є ще ${n} ${plural(n, 'запис', 'записи', 'записів')}, дуже схожих на цей.`,
      samePhone: 'той самий телефон',
      sameName: 'те саме імʼя й дата народження',
      merge: 'Обʼєднати',
      askManager: 'Обʼєднати записи може керуючий клубом або директор.',
      mergeTitle: 'Обʼєднати два записи?',
      mergeMessage:
        'Лишиться запис, який ви оберете. Порожні поля в ньому доповнимо з другого, згоди й нотатки перейдуть до нього. Другий запис не зникне — він вказуватиме на обраний.',
      keep: 'Який запис лишити',
      keepThis: 'Цей, відкритий',
      keepThat: 'Знайдений схожий',
      survivor: (line: string) => `Лишиться: ${line}`,
      added: (date: string) => `додано ${date}`,
      mergeConfirm: 'Обʼєднати',
      merged: 'Записи обʼєднано.',
    },

    erase: {
      action: 'Стерти дані клієнта',
      title: (name: string) => `Стерти дані: ${name}?`,
      message:
        'Імʼя, телефон, пошту, дату народження й нотатки буде видалено назавжди, а картку перенесено в архів. Відвідування й оплати лишаться в історії клубу без особи. Скасувати це не можна.',
      confirm: 'Стерти назавжди',
    },

    form: {
      addTitle: 'Новий клієнт',
      editTitle: 'Змінити клієнта',
      person: 'Хто це',
      contacts: 'Контакти',
      firstName: 'Імʼя',
      firstNamePlaceholder: 'Олена',
      lastName: 'Прізвище',
      lastNamePlaceholder: 'Коваленко',
      phone: 'Телефон',
      phonePlaceholder: '067 123 45 67',
      phoneForeign: 'Не схоже на український номер — збережемо, як написано.',
      email: 'Пошта',
      emailPlaceholder: 'name@example.com',
      birthDate: 'Дата народження',
      cardNumber: 'Номер картки',
      cardNumberPlaceholder: 'Браслет або пластикова картка',
      more: 'Більше про клієнта',
      branch: 'Клуб',
      branchHint: '«Уся мережа» — клієнта бачать і обслуговують у кожному клубі.',
      wholeNetwork: 'Уся мережа',
      source: 'Звідки дізнався',
      sourcePlaceholder: 'Інстаграм, друг, проходив повз',
      tags: 'Мітки',
      tagsPlaceholder: 'Через кому: ранкові, йога',
      note: 'Коротка примітка',
      notePlaceholder: 'Видно одразу на картці. Для довшого — нотатки',
      certificate: 'Медична довідка дійсна до',
      certificateHint: 'Порожньо — довідки немає.',
      consents: 'Згоди',
      consentsFootnote: 'Позначте, лише якщо клієнт погодився — усно чи письмово. Змінити можна будь-коли на картці.',
      submitAdd: 'Додати клієнта',
      submitAdding: 'Додаємо…',
      consentsFailed: 'Клієнта додано, але згоди не записалися. Позначте їх ще раз на картці.',
      errors: {
        identify: 'Вкажіть імʼя, телефон або номер картки',
        tooLong: (max: number) => `Не більше ${max} символів`,
        email: 'Це не схоже на пошту',
        date: 'Дата має бути у форматі РРРР-ММ-ДД',
        birthInFuture: 'Дата народження не може бути в майбутньому',
        tagTooLong: 'Кожна мітка — не довша за 40 символів',
        tooManyTags: 'Не більше 30 міток',
      },
    },
  },

  timezone: {
    label: 'Часовий пояс',
    hint: 'Від нього залежить, о котрій заняття й до якого дня потрапляє продаж.',
    change: 'Змінити',
    search: 'Пошук: місто, Europe/… або +2',
    suggested: 'Найчастіші',
    all: 'Усі пояси',
    noMatches: 'Такого поясу не знайдено',
    current: 'Обрано',
  },

  lookup: {
    find: 'Знайти',
    finding: 'Шукаємо…',
    change: 'Змінити',
    placeholder: 'name@example.com',
    alreadyMember: (role: string) => `Уже в компанії: ${role}`,
    alreadyGod: 'Уже бог',
  },

  gods: {
    title: 'Боги',
    subtitle:
      'Команда eatlete: бачать і змінюють будь-яку компанію, а клуби бачать їхні зміни від «eatlete».',
    add: 'Додати бога',
    bootstrap: 'Перший, із сервера',
    remove: 'Зняти',
    removeTitle: (name: string) => `Зняти бога: ${name}?`,
    removeMessage:
      'Людина втратить доступ до всіх компаній, яких вона не є членом. Повернути зможе будь-який інший бог.',
    removeSelfMessage:
      'Ви втратите доступ до консолі богів і до компаній, у яких ви не член. Повернути зможе інший бог.',
    removeConfirm: 'Зняти',
    form: {
      title: 'Новий бог',
      email: 'Пошта',
      hint: 'Бог бачить і змінює будь-яку компанію. Додавайте лише людей із команди eatlete.',
      submit: 'Зробити богом',
      submitting: 'Додаємо…',
    },
    unknownName: 'Імʼя недоступне',
    columns: { name: 'Імʼя', email: 'Пошта', granted: 'Надано' },
  },

  staff: {
    title: 'Співробітники',
    count: (n: number) => people(n),
    add: 'Додати співробітника',
    empty: 'Тут ще нікого немає',
    emptyMessage:
      'Додайте людей за поштою їхнього акаунта eatlete, запросіть посиланням або додайте тренера без акаунта.',
    noAccount: 'Без акаунта',
    noAccountHint: 'Людина ще не входить у панель — її можна запросити на пошту',
    allBranches: 'Усі клуби',
    noBranches: 'Без клубів',
    archivedBranch: (name: string) => `${name} (архів)`,
    changeRole: 'Змінити роль',
    edit: 'Змінити',
    remove: 'Прибрати з компанії',
    removeTitle: (name: string) => `Прибрати: ${name}?`,
    removeMessage: 'Людина одразу втратить доступ до компанії. Додати її знову можна будь-коли.',
    removeConfirm: 'Прибрати',
    notBelowYou: 'Роль не нижча за вашу — змінити не можна',
    ownerOnlyHint: 'Власників змінюють лише власники',
    columns: { name: 'Імʼя', title: 'Посада', role: 'Роль', branches: 'Клуби', since: 'У компанії з' },
    photoOf: (name: string) => `Фото: ${name}`,
    created: (name: string) => `${name} — у списку співробітників.`,
    createdPhotoFailed: (name: string, reason: string) =>
      `${name} — у списку співробітників, але фото не завантажилось: ${reason}`,
    invited: (email: string) => `Запрошення для ${email} створено.`,
    /// The person's side panel (specs/staff-profiles.md §2).
    profile: {
      photo: 'Фото',
      photoHint: 'PNG, JPG або WebP, до 5 МБ. Найкраще — портрет, обличчя по центру.',
      photoChoose: 'Додати фото',
      photoReplace: 'Замінити',
      photoRemove: 'Прибрати',
      photoDrop: 'Відпустіть, щоб додати',
      photoUploaded: 'Фото оновлено.',
      photoRemoved: 'Фото прибрано.',
      photoErrors: {
        type: 'Підходять лише PNG, JPG або WebP. SVG та інші формати не приймаємо',
        size: 'Файл більший за 5 МБ. Зменште фото й спробуйте ще раз',
        empty: 'Файл порожній. Оберіть інший',
      },
      title: 'Посада',
      titlePlaceholder: 'Персональний тренер',
      titleHint: 'Так її побачать клієнти, коли обиратимуть виконавця.',
      bio: 'Про себе',
      bioPlaceholder: 'Досвід, спеціалізація, сертифікати — те, що клієнт прочитає перед записом',
      bioCount: (n: number, max: number) => `${n.toLocaleString('uk-UA')} / ${max.toLocaleString('uk-UA')}`,
      tooLong: (max: number) => `Не довше ${max.toLocaleString('uk-UA')} символів`,
      nameRequired: 'Введіть імʼя — за ним клуб упізнаватиме людину',
      standing: 'Роль і клуби',
      standingReadOnly: 'Роль і клуби змінює керівник.',
      selfNote: 'Посаду, опис і фото ви змінюєте самі. Їх бачать колеги й клієнти.',
      saved: 'Профіль збережено.',
      noAccountTitle: 'Без акаунта',
      noAccountMessage:
        'Цю людину можна ставити в розклад, призначати виконавцем послуг і нараховувати їй оплату, але увійти в панель вона не може, доки її не запросять.',
      invite: 'Запросити на email',
    },
    /// «Мій профіль» — a member's own profile, whatever their role.
    me: {
      title: 'Мій профіль',
      subtitle: 'Як вас бачать колеги й клієнти клубу.',
      notMember: 'Профіль є лише в тих, хто працює в компанії.',
      save: 'Зберегти профіль',
    },
    /// Adding without an account (specs/staff-profiles.md §3).
    addMode: {
      label: 'Як додати',
      account: 'За поштою',
      profile: 'Без акаунта',
      profileExplain:
        'Таку людину можна ставити в розклад, призначати виконавцем послуг і нараховувати оплату, але увійти вона не зможе, доки її не запросять.',
    },
    /// Inviting a profile without an account to take it over (§4).
    inviteProfile: {
      title: (name: string) => `Запросити: ${name}`,
      emailHint: 'На цю пошту людина увійде або створить акаунт eatlete.',
      explain:
        'Коли людина прийме запрошення, її розклад, послуги й нарахування перейдуть на її акаунт. Роль і клуби — ті, що в профілі зараз.',
      submit: 'Створити посилання',
    },
    form: {
      addTitle: 'Новий співробітник',
      editTitle: 'Співробітник',
      email: 'Пошта',
      emailHint: 'Пошта акаунта eatlete. Немає акаунта — надішлемо запрошення.',
      name: 'Імʼя в компанії',
      namePlaceholder: 'Як його бачитимуть колеги',
      role: 'Роль',
      roleHint: 'Лише ролі, нижчі за вашу.',
      chooseRole: 'Оберіть роль',
      branches: 'Клуби',
      branchesHint: 'Для ролей, що працюють у своїх клубах. Нічого не обрано — усі клуби.',
      branchesAll: 'Усі клуби',
      branchesSome: (n: number, of: number) => `${n} з ${of}`,
      archivedHint: 'В архіві — зніміть позначку, щоб прибрати',
      submitAdd: 'Додати',
      submitAdding: 'Додаємо…',
      roleRequired: 'Оберіть роль',
      personRequired: 'Знайдіть людину за поштою',
    },
  },

  invites: {
    invite: 'Запросити',
    inviteByEmail: 'Запросити за поштою',
    sendInvite: 'Надіслати запрошення',
    noAccount: 'Акаунта з цією поштою ще немає',
    noAccountMessage: 'Запросіть людину посиланням — акаунт вона створить, коли відкриє його.',
    section: 'Запрошення',
    columns: { person: 'Кому', role: 'Роль', state: 'Стан', by: 'Запросив(-ла)' },
    state: {
      pending: (date: string) => `Чекає до ${date}`,
      expired: (date: string) => `Прострочено ${date}`,
      accepted: (date: string) => `Прийнято ${date}`,
    },
    acceptedFootnote: 'Прийняті запрошення зникають звідси за кілька днів — людина вже в «Співробітниках».',
    sectionFootnote:
      'Посилання діє 7 днів і лише для своєї пошти. Щоб надіслати ще раз, зробіть нове посилання — старе перестане діяти.',
    newLink: 'Нове посилання',
    revoke: 'Відкликати',
    revokeTitle: (email: string) => `Відкликати запрошення для ${email}?`,
    revokeMessage: 'Посилання одразу перестане діяти. Запросити знову можна будь-коли.',
    revokeConfirm: 'Відкликати',
    revoked: (email: string) => `Запрошення для ${email} відкликано.`,
    form: {
      title: 'Запрошення',
      email: 'Пошта',
      emailHint: 'На цю пошту людина увійде або створить акаунт — інша не підійде.',
      emailRequired: 'Введіть пошту',
      emailInvalid: 'Це не схоже на пошту',
      submit: 'Створити посилання',
      submitting: 'Створюємо…',
    },
    ready: {
      title: 'Посилання готове',
      renewedTitle: 'Нове посилання готове',
      message: (email: string) =>
        `Надішліть його ${email} у Telegram, Viber чи поштою. Посилання показуємо лише зараз — потім можна зробити нове.`,
      emailed: (email: string) => `Ми також надіслали його на ${email}.`,
      link: 'Посилання',
      text: 'Готовий текст',
      copy: 'Копіювати',
      copyText: 'Копіювати текст',
      copied: 'Скопійовано',
      expires: (date: string) => `Діє до ${date} і лише один раз.`,
      done: 'Готово',
    },
    /// What the owner pastes into a messenger.
    messageText: (company: string, role: string | null, url: string, date: string) =>
      `Вітаю! Запрошую вас до команди «${company}» в eatlete${role ? ` — роль «${role}»` : ''}.\n` +
      `Відкрийте посилання, увійдіть або створіть акаунт на цю пошту й прийміть запрошення:\n${url}\n` +
      `Посилання діє до ${date}.`,
  },

  invitePage: {
    title: 'Запрошення до команди',
    lead: (company: string) => `Вас запрошують працювати в «${company}».`,
    role: 'Роль',
    invitedBy: 'Запросив(-ла)',
    email: 'Для пошти',
    expires: 'Діє до',
    signInTitle: 'Увійдіть, щоб прийняти',
    signInMessage: 'Увійдіть акаунтом eatlete з цією поштою або створіть новий — це хвилина.',
    signIn: 'Увійти',
    signUp: 'Створити акаунт',
    signedInAs: (email: string) => `Ви ввійшли як ${email}.`,
    accept: 'Прийняти запрошення',
    accepting: 'Приймаємо…',
    mismatchTitle: 'Це запрошення для іншої пошти',
    mismatchMessage: (masked: string, mine: string) =>
      `Запрошення — для ${masked}, а ви ввійшли як ${mine}. Вийдіть і ввійдіть потрібним акаунтом або створіть його.`,
    signOut: 'Вийти й увійти іншим акаунтом',
    expiredTitle: 'Термін запрошення минув',
    expiredMessage: 'Попросіть того, хто вас запросив, зробити нове посилання.',
    revokedTitle: 'Запрошення відкликано',
    revokedMessage: 'Посилання більше не діє. Якщо це помилка, попросіть нове.',
    acceptedTitle: 'Запрошення вже прийнято',
    acceptedMessage: 'Посилання одноразове. Якщо це були ви — увійдіть у панель.',
    unknownTitle: 'Такого запрошення немає',
    unknownMessage: 'Перевірте, що посилання скопійовано повністю, або попросіть нове.',
    staleTitle: 'Запрошення застаріло',
    staleMessage: 'Ролі чи клубу з нього вже немає. Попросіть того, хто вас запросив, надіслати нове.',
    alreadyMemberTitle: 'Ви вже в цій команді',
    toPanel: 'До панелі',
    signUpForm: {
      title: 'Новий акаунт eatlete',
      message: 'З ним ви ввійдете і сюди, і в застосунок eatlete.',
      name: 'Імʼя та прізвище',
      namePlaceholder: 'Олена Коваль',
      email: 'Пошта',
      emailHint: 'Та сама, на яку надіслано запрошення.',
      password: 'Пароль',
      passwordPlaceholder: 'Щонайменше 8 символів',
      submit: 'Створити акаунт і прийняти',
      submitting: 'Створюємо…',
      haveAccount: 'Уже маєте акаунт? Увійти',
      errors: {
        emptyName: 'Як вас звати?',
        shortPassword: 'Пароль — щонайменше 8 символів',
        emailTaken: 'Акаунт із цією поштою вже є. Увійдіть ним',
        failed: 'Не вдалося створити акаунт. Спробуйте ще раз',
      },
    },
  },

  roles: {
    title: 'Ролі',
    subtitle: 'Хто що може в компанії. Системні ролі однакові для всіх клубів; власні можна будувати.',
    system: 'Системні',
    custom: 'Власні',
    noCustom: 'Власних ролей ще немає',
    noCustomMessage: 'Скопіюйте системну роль або почніть з нуля.',
    create: 'Нова роль',
    copy: 'Копіювати',
    copyName: (name: string) => `${name} (копія)`,
    holders: (n: number) => (n === 0 ? 'Нікого' : people(n)),
    readOnly: 'Системна роль — лише для перегляду. Скопіюйте її, щоб змінити.',
    aboveYou: 'Ця роль має більше, ніж ваша, тож змінити чи видалити її не можна.',
    byEatlete: 'Створено eatlete',
    eatlete: 'eatlete',
    columns: { name: 'Назва', scope: 'Охоплення', people: 'Люди' },
    nobody: '—',
    builder: {
      newTitle: 'Нова роль',
      name: 'Назва',
      namePlaceholder: 'Наприклад, «Адмін вихідного дня»',
      permissions: 'Доступ',
      permissionsFootnote:
        'Рівень вище за ваш обрати не можна — роль не може мати більше прав, ніж ви.',
      module: 'Розділ',
      scope: 'Охоплення',
      scopeFootnote: 'Що саме бачить людина: лише те, до чого вона причетна, свої клуби чи всю компанію.',
      limits: 'Ліміти',
      limitsFootnote:
        'Діють, коли «Продажі» — «Зміни». На «Керуванні» обмежень немає, нижче — нуль. Порожнє поле — без обмежень.',
      discount: 'Найбільша знижка',
      refund: 'Найбільше повернення',
      unlimited: 'Без обмежень',
      ceiling: (amount: string) => `Не більше ${amount} — це ваш ліміт`,
      save: 'Зберегти роль',
      create: 'Створити роль',
      delete: 'Видалити роль',
      deleteTitle: (name: string) => `Видалити роль «${name}»?`,
      deleteMessage: 'Роль зникне з компанії. Це не можна скасувати.',
      deleteConfirm: 'Видалити',
      nameRequired: 'Назвіть роль',
      discountInvalid: 'Від 0 до 100',
      refundInvalid: 'Лише цифри, наприклад 500 або 499,90',
      aboveYours: 'Вище за ваш рівень',
      exceeds: 'Більше, ніж у вас',
      notApplicable: 'Не буває',
      changedNotSaved: 'Є незбережені зміни',
      level: 'Рівень',
      noAccess: 'Немає доступу',
      allows: 'Що може',
      legend:
        'Перегляд — лише бачить. Зміни — щоденна робота. Керування — ще й налаштовує та виправляє.',
      levelOf: (module: string, level: string) => `${module}: ${level}`,
      limitUnlimited: 'Без обмежень',
      limitNone: 'Немає',
      limitDiscount: (pct: string) => `До ${pct} %`,
      limitRefund: (uah: string) => `До ${uah} ₴`,
    },
  },

  modules: {
    clients: 'Клієнти',
    checkin: 'Вхід',
    sales: 'Продажі',
    cash: 'Каса',
    passes: 'Абонементи',
    schedule: 'Розклад',
    trainings: 'Тренування',
    stock: 'Склад',
    payroll: 'Зарплата',
    finance: 'Фінанси',
    reports: 'Звіти',
    staff: 'Персонал',
    settings: 'Налаштування',
  },

  /// What each level of each module allows — specs/roles.md §3, in words.
  moduleLevels: {
    clients: { view: 'Бачить клієнтів', edit: 'Реєструє й змінює', manage: 'Обʼєднує, видаляє, вивантажує' },
    checkin: { view: 'Бачить візити', edit: 'Пропускає на вході', manage: 'Пропускає попри відмову' },
    sales: { view: 'Бачить продажі', edit: 'Продає в межах лімітів', manage: 'Повернення й знижки понад ліміт' },
    cash: { view: 'Бачить зміни', edit: 'Відкриває й закриває зміну', manage: 'Приймає розбіжність у касі' },
    passes: { view: 'Бачить абонементи', edit: 'Заморожує, подовжує', manage: 'Створює абонементи й ціни' },
    schedule: { view: 'Бачить розклад', edit: 'Записує, переносить', manage: 'Створює заняття й шаблони' },
    trainings: { view: 'Бачить тренування', edit: 'Підтверджує, що тренування було', manage: 'Виправляє підтверджені' },
    stock: { view: 'Бачить склад', edit: 'Приймає товар', manage: 'Списання, номенклатура' },
    payroll: { view: 'Бачить зарплату', edit: 'Вносить нарахування', manage: 'Ставки й виплати' },
    finance: { view: 'Прибутки, збитки, журнал', edit: '', manage: 'Періоди, ФОП і термінали' },
    reports: { view: 'Бачить звіти', edit: '', manage: 'Вивантажує звіти' },
    staff: { view: 'Бачить персонал', edit: 'Призначає ролі тим, хто нижче', manage: 'Будує ролі' },
    settings: { view: 'Бачить налаштування', edit: 'Змінює налаштування клубу', manage: 'Клуби, інтеграції' },
  },

  levels: {
    none: 'Немає',
    view: 'Перегляд',
    edit: 'Зміни',
    manage: 'Керування',
  },

  scopes: {
    own: 'Лише своє',
    branches: 'Свої клуби',
    company: 'Уся компанія',
  },

  limitsShort: {
    discount: (pct: number | null) => (pct === null ? 'знижка без меж' : `знижка до ${pct} %`),
    refund: (uah: string | null) => (uah === null ? 'повернення без меж' : `повернення до ${uah} ₴`),
  },

  errors: {
    network: 'Немає звʼязку із сервером. Перевірте інтернет і спробуйте ще раз',
    server: 'Щось пішло не так на сервері. Спробуйте ще раз',
    sessionEnded: 'Сесія завершилася. Увійдіть ще раз',
    generic: 'Не вдалося. Спробуйте ще раз',
    invalidRequest: 'Перевірте поля — сервер їх не прийняв',
    notGod: 'Це може лише команда eatlete',
    forbidden: 'Вашій ролі це недоступно',
    notBelowYou: 'Можна призначати, змінювати чи прибирати лише тих, чия роль нижча за вашу — і давати лише нижчі ролі',
    ownerOnly: 'Призначати, змінювати й прибирати власників можуть лише власники',
    lastOwner: 'У компанії має лишитися хоча б один власник. Спершу призначте іншого',
    escalation: (what: string) => `Роль не може мати більше прав, ніж ваша. Більше, ніж у вас: ${what}`,
    roleInUse: (n: number) =>
      `Цю роль досі має ${people(n)}. Спершу дайте ${n === 1 ? 'їй' : 'їм'} іншу роль`,
    roleNameTaken: 'Роль із такою назвою вже є — у тому числі серед системних',
    systemRole: 'Системні ролі не змінюються. Скопіюйте роль і змініть копію',
    unknownRole: 'Такої ролі вже немає. Оновіть сторінку',
    unknownUser:
      'Акаунта з такою поштою в eatlete немає. Людина має спершу зареєструватися в застосунку',
    alreadyMember: 'Ця людина вже працює в компанії',
    alreadyGod: 'Ця людина вже бог',
    lastGod: 'Останнього бога зняти не можна — спершу додайте іншого',
    unknownGod: 'Ця людина вже не бог',
    unknownOrg: 'Компанію не знайдено, або у вас немає до неї доступу',
    unknownMember: 'Цієї людини вже немає в компанії',
    profileHasAccount: 'У цієї людини вже є акаунт — запрошувати до профілю не потрібно',
    unknownBranch: 'Такого клубу в компанії немає. Оновіть сторінку й оберіть клуб ще раз',
    branchArchived:
      'Цей клуб в архіві — нових людей до нього не привʼязують. Поверніть його з архіву в «Клубах» або оберіть інший',
    orgSuspended: 'Компанію призупинено',
    authUnavailable: 'Не вдалося звернутися до акаунтів eatlete. Спробуйте ще раз',
    tooMany: 'Забагато спроб. Спробуйте за кілька хвилин',
    outOfScope: 'Ви працюєте в обраних клубах — можна запрошувати й змінювати лише людей у них',
    invitePending:
      'Цю пошту вже запросив хтось із вищою роллю. Попросіть його відкликати запрошення або дочекайтеся, поки воно спливе',
    inviteClosed: 'Запрошення вже прийняте або відкликане. Створіть нове',
    unknownInvite: 'Такого запрошення вже немає. Оновіть сторінку',
    inviteExpired: 'Термін запрошення минув. Попросіть нове посилання',
    inviteRevoked: 'Запрошення відкликано',
    inviteAccepted: 'Це запрошення вже прийнято',
    inviteEmailMismatch: 'Це запрошення для іншої пошти',
    inviteStale: 'Ролі чи клубу із запрошення вже немає. Попросіть нове',
    unknownClient: 'Такого клієнта немає — можливо, його видалили або він в іншому клубі',
    clientMerged: 'Цей запис уже обʼєднано з іншим. Відкрийте запис, що лишився',
    alreadyMerged: 'Один із записів уже обʼєднано з іншим. Оновіть сторінку',
    scheduleUnavailable: 'Не вдалося дізнатися, хто ходить на ваші заняття. Спробуйте за хвилину',
    orgsUnavailable: 'Не вдалося перевірити ваш доступ. Спробуйте ще раз',
    clientOutOfScope: 'Цей клуб поза вашими клубами — оберіть свій або «Уся мережа»',
    scope: 'охоплення',
    maxDiscountPct: 'знижка',
    maxRefund: 'повернення',
    owner: 'дії власника',
  },

  // «Абонементи» — its own file (uk-passes.ts), one line here.
  passes: passesUk,

  // «Вхід» — its own file (uk-access.ts), one line here.
  access: accessUk,

  // «Послуги» — its own file (uk-services.ts), one line here.
  services: servicesUk,

  // Online pass sale and the club on «Зали поруч» (uk-sale.ts).
  sale: saleUk,

  // «Вхід за обличчям»: the enrolment check and the kiosk (uk-face.ts).
  face: faceUk,

  // «Товари» — its own file (uk-goods.ts), one line here.
  goods: goodsUk,

  // «Каса» — its own file (uk-cash.ts), one line here.
  cash: cashUk,
  // «Огляд» — its own file (uk-overview.ts), one line here.
  overview: overviewUk,
  // «Зарплата» and «Моя робота» — its own file (uk-payroll.ts), one line here.
  payroll: payrollUk,

  design: {
    title: 'Дизайн-система',
    subtitle: 'Усі компоненти панелі в обох темах. Тут нічого не зберігається.',
  },
}

export type Strings = typeof uk
